/**
 * TCF 2.3 Consent Banner
 * Main entry point
 */

// Import CSS (will be injected by build)
import '../css/banner.css';

// Import modules
import { initTCFApi, setGdprApplies } from './tcf-api.js';
import { initBannerUI, showBanner, openSettings, destroyBannerUI } from './banner-ui.js';
import { hasConsent, clearConsent, getStoredConsent, getTCString } from './consent-storage.js';
import { getVendorCount, initGVL } from './gvl-data.js';
import { initTheme, setTheme, themes, getCurrentTheme } from './themes.js';
import { initGoogleConsentMode, disableGoogleAnalytics, enableGoogleAnalytics } from './vendor-config.js';

/**
 * Configuration options
 */
const defaultConfig = {
  gdprApplies: true,
  autoShow: true,
  transparencyUrl: null,
  onConsentChange: null,
  theme: null // auto-detect if null
};

let config = { ...defaultConfig };
let currentTheme = 'default';

/**
 * Initialize the TCF Consent Banner
 * @param {Object} options - Configuration options
 */
function init(options = {}) {
  config = { ...defaultConfig, ...options };
  
  // Set GDPR applies
  setGdprApplies(config.gdprApplies);
  
  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeComponents();
    });
  } else {
    initializeComponents();
  }
}

/**
 * Initialize all components
 */
async function initializeComponents() {
  // Initialize theme first (affects UI appearance)
  if (config.theme && themes[config.theme]) {
    currentTheme = setTheme(config.theme);
  } else {
    currentTheme = initTheme();
  }
  
  // Initialize TCF API first (vendors need this)
  initTCFApi();
  
  // Load the Global Vendor List from IAB
  console.log('Loading Global Vendor List from IAB...');
  await initGVL();
  
  // Initialize Banner UI
  initBannerUI();
  
  // Log initialization
  console.log('TCF 2.3 Consent Banner initialized');
  console.log(`Theme: ${currentTheme}`);
  console.log(`Vendor count: ${getVendorCount()}`);
  console.log(`Consent exists: ${hasConsent()}`);
  
  if (hasConsent()) {
    console.log(`TC String: ${getTCString()}`);
  }
}

/**
 * Show the consent banner manually
 */
function show() {
  showBanner();
}

/**
 * Open the settings/preferences modal
 */
function showSettings() {
  openSettings();
}

/**
 * Reset/clear all consent
 */
function reset() {
  clearConsent();
  destroyBannerUI();
  initBannerUI();
}

/**
 * Get current consent status
 */
function getConsent() {
  return getStoredConsent();
}

/**
 * Check if consent has been given
 */
function hasUserConsent() {
  return hasConsent();
}

/**
 * Get the current TC String
 */
function getCurrentTCString() {
  return getTCString();
}

// Export public API
const TCFBanner = {
  init,
  show,
  showSettings,
  reset,
  getConsent,
  hasUserConsent,
  getTCString: getCurrentTCString,
  // Theme API
  setTheme,
  getTheme: getCurrentTheme,
  themes: Object.keys(themes),
  // Google Consent Mode helpers
  initGoogleConsentMode,
  disableGoogleAnalytics,
  enableGoogleAnalytics,
  version: '1.0.0'
};

// Attach to window FIRST for non-module usage
if (typeof window !== 'undefined') {
  window.TCFBanner = TCFBanner;
}

// Auto-initialize if data attribute is present
const currentScript = document.currentScript;
if (!currentScript || currentScript.dataset?.autoInit !== 'false') {
  // Check for script config
  const scriptConfig = currentScript?.dataset;
  if (scriptConfig) {
    const options = {};
    if (scriptConfig.gdprApplies !== undefined) {
      options.gdprApplies = scriptConfig.gdprApplies !== 'false';
    }
    if (scriptConfig.tcfTheme !== undefined) {
      options.theme = scriptConfig.tcfTheme;
    }
    init(options);
  } else {
    init();
  }
}

// Export for module usage
export default TCFBanner;



